import {
  type TBrandApi,
  type TBrandDetailedApi,
  type TBrandDto
} from '@/components/AdminPanel/Auto/Brands/types';
import {
  type ICreateUsedCarBody,
  type IGetUsedCarsParams,
  type IUsedCar,
  type TCreateEquipment,
  type TEquipment,
  type TPackage,
  type TPackageProducts,
  type TStock,
  type TStockCreate,
  type TStockDetailed
} from '@/types/model.type';

import { ApiConnector } from '../api.connector';

class ModelsApi extends ApiConnector {
  // ------- Brands -------
  public getBrands(
    limit?: number,
    page?: number,
    searchTerm?: string
  ): Promise<{
    total: number;
    data: TBrandApi[];
  }> {
    return this.call({
      path: 'admin/brands',
      params: {
        limit,
        page,
        searchTerm
      },
      cancelable: true
    });
  }

  public getBrandById(brandId: string): Promise<TBrandDetailedApi> {
    return this.call({ path: `admin/brands/${brandId}`, cancelable: true });
  }

  public createBrand(data: TBrandDto) {
    return this.call({ path: 'admin/brands', method: 'post', body: data });
  }

  public updateBrand(data: TBrandDto) {
    return this.call({
      path: `admin/brands/${data.uuid}`,
      method: 'patch',
      body: data
    });
  }

  // ------- Stocks -------
  public getStocks(
    limit?: number,
    page?: number,
    searchTerm?: string,
    dealer?: string,
    brand?: string,
    model?: string
  ): Promise<{
    total: number;
    data: TStock[];
  }> {
    return this.call({
      path: 'admin/stocks',
      params: {
        limit,
        page,
        searchTerm,
        dealer,
        brand,
        model
      },
      cancelable: true
    });
  }

  public getStockById(stockId: string): Promise<TStockDetailed> {
    return this.call({ path: `admin/stocks/${stockId}`, cancelable: true });
  }

  public createStock(data: TStockCreate): Promise<{ uuid: string }> {
    return this.call({ path: 'admin/stocks', method: 'post', body: data });
  }

  public updateStock(
    stockId: string,
    data: TStockCreate
  ): Promise<{ uuid: string }> {
    return this.call({
      path: `admin/stocks/${stockId}`,
      method: 'patch',
      body: data
    });
  }

  // ------- Used Cars -------
  public getUsedCars(
    params: IGetUsedCarsParams
  ): Promise<{ total: number; data: IUsedCar[] }> {
    return this.call({
      path: 'admin/used-cars',
      params,
      cancelable: true
    });
  }

  public getUsedCarById(usedCarId: string): Promise<IUsedCar> {
    return this.call({
      path: `admin/used-cars/${usedCarId}`,
      cancelable: true
    });
  }

  public createUsedCar(data: ICreateUsedCarBody): Promise<{ uuid: string }> {
    return this.call({ path: 'admin/used-cars', method: 'post', body: data });
  }

  public updateUsedCar(
    usedCarId: string,
    data: Partial<ICreateUsedCarBody>
  ): Promise<{ uuid: string }> {
    return this.call({
      path: `admin/used-cars/${usedCarId}`,
      method: 'put',
      body: data
    });
  }

  // ------- Equipments -------
  public getEquipments(
    limit?: number,
    page?: number,
    searchTerm?: string,
    brand?: string,
    model?: string,
    isActive?: boolean
  ): Promise<{
    total: number;
    data: TEquipment[];
  }> {
    return this.call({
      path: 'admin/equipments',
      params: {
        limit,
        page,
        searchTerm,
        brand,
        model,
        isActive
      },
      cancelable: true
    });
  }

  public getEquipmentById(equipmentId: string): Promise<TEquipment> {
    return this.call({
      path: `admin/equipments/${equipmentId}`,
      cancelable: true
    });
  }

  public createEquipment(data: TCreateEquipment) {
    return this.call({ path: 'admin/equipments', method: 'post', body: data });
  }

  public updateEquipment(equipmentId: string, data: TCreateEquipment) {
    return this.call({
      path: `admin/equipments/${equipmentId}`,
      method: 'patch',
      body: data
    });
  }

  // ------- Packages -------
  public getPackages(
    limit?: number,
    offset?: number,
    searchTerm?: string
  ): Promise<{
    total: number;
    data: TPackage[];
  }> {
    return this.call({
      path: 'admin/extra-product-packages',
      params: {
        limit,
        offset,
        searchTerm
      },
      cancelable: true
    });
  }

  public getPackageById(packageId: string): Promise<TPackage[]> {
    return this.call({
      path: `admin/extra-product-packages/${packageId}`,
      cancelable: true
    });
  }

  public createPackage(data: TPackage) {
    return this.call({
      path: 'admin/extra-product-packages',
      method: 'post',
      body: data
    });
  }

  public updatePackage(packageId: string, data: TPackage) {
    return this.call({
      path: `admin/extra-product-packages/${packageId}`,
      method: 'patch',
      body: data
    });
  }

  // ------- Package Products -------
  public getPackageProducts(
    packageId: string,
    limit?: number,
    offset?: number,
    searchTerm?: string
  ): Promise<{
    total: number;
    data: TPackageProducts[];
  }> {
    return this.call({
      path: `admin/extra-product-packages/${packageId}/products`,
      params: {
        limit,
        offset,
        searchTerm
      },
      cancelable: true
    });
  }

  public getPackageProductById(productId: string): Promise<TPackageProducts> {
    return this.call({
      path: `admin/extra-product-packages/products/${productId}`,
      cancelable: true
    });
  }

  public createPackageProduct(data: TPackageProducts) {
    const { uuid, ...body } = data;
    return this.call({
      path: 'admin/extra-product-packages/products',
      method: 'post',
      body: {
        ...body,
        packageId: uuid
      }
    });
  }

  public updatePackageProduct(productId: string, data: TPackageProducts) {
    return this.call({
      path: `admin/extra-product-packages/products/${productId}`,
      method: 'patch',
      body: data
    });
  }
}

export const modelsApi = new ModelsApi();
